import type { BuyboxOffers } from '@/interfaces/buybox';
import { BuyboxOfferCategory } from '@/interfaces/buybox';
import type Provider from '@/interfaces/provider';
import { OfferMonetizationType, OfferPresentationType, TitleObjectType } from '@/interfaces/titles';

import type { SportObjectType } from '@/@types/graphql-types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';

import type { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowClickoutContext, SnowplowTitleContext } from '@/helpers/tracking/providers';
import type { Offer } from '@/interfaces/titles';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

function getPriceForOffer(offer: Offer, locale: string): string {
	if (offer.retail_price !== undefined) {
		return offer.retail_price
			.toLocaleString(locale, {
				style: 'currency',
				currency: offer.currency,
			})
			.replace(/\s+/, ''); // The space is actually a non-breaking-space D:
	}
	return '';
}

function getUctLink(
	offer: Offer,
	provider: Provider,
	country: string,
	buyboxParam: BuyboxParam,
	domainSessionId?: string
) {
	const clickout = new ClickoutUrl({ standardWebURL: encodeURIComponent(offer.urls.standard_web) })
		.set('uct_country', country)
		.set('uct_buybox', buyboxParam)
		.set('sid', domainSessionId);

	if (offer.monetization_type !== OfferMonetizationType.BUNDLE) {
		clickout.setContexts(SnowplowClickoutContext.fromProviderOffer(offer, provider));
	}

	return clickout.toString();
}

/**
 * translatable right unter the provider icon, e.g. STREAM or RENT or TICKET.
 */
function getTranslatableForOffer(
	offer: Offer,
	objectType: TitleObjectType
): false | { name: string; properties?: any } {
	if (offer.element_count && (offer.type === 'aggregated' || (offer.type !== 'aggregated' && !offer.retail_price))) {
		if (offer.element_count > 1) {
			if (objectType === TitleObjectType.SHOW) {
				return { name: 'WEBAPP_X_SEASONS', properties: { seasons: offer.element_count } };
			} else if (objectType === TitleObjectType.SEASON) {
				return { name: 'WEBAPP_X_EPISODES', properties: { episodes: offer.element_count } };
			}
		} else if (offer.element_count === 1) {
			if (objectType === TitleObjectType.SHOW) {
				return { name: 'WEBAPP_ONE_SEASON' };
			} else if (objectType === TitleObjectType.SEASON) {
				return { name: 'WEBAPP_ONE_EPISODE' };
			}
		}
	}
	if (offer.monetization_type in MONETIZATION_LABEL_TRANSLATABLES) {
		return { name: (MONETIZATION_LABEL_TRANSLATABLES as any)[offer.monetization_type] };
	}
	return false;
}

const CATEGORY_MONETIZATIONS: Record<BuyboxOfferCategory, OfferMonetizationType[]> = {
	[BuyboxOfferCategory.STREAM]: [
		OfferMonetizationType.FLATRATE,
		OfferMonetizationType.FREE,
		OfferMonetizationType.ADS,
		OfferMonetizationType.FLATRATE_AND_BUY,
	],
	[BuyboxOfferCategory.RENT]: [OfferMonetizationType.RENT],
	[BuyboxOfferCategory.BUY]: [OfferMonetizationType.BUY],
	[BuyboxOfferCategory.PHYSICAL]: [OfferMonetizationType.BUY],
	[BuyboxOfferCategory.BUNDLE]: [OfferMonetizationType.BUNDLE],
	[BuyboxOfferCategory.CINEMA]: [OfferMonetizationType.CINEMA],
};

const PRESENTATION_TYPE_PRIORITY = [OfferPresentationType._4K, OfferPresentationType.HD, OfferPresentationType.SD];

const CATEGORY_PRESENTATION_TYPES = {
	[BuyboxOfferCategory.STREAM]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.RENT]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.BUY]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.PHYSICAL]: [OfferPresentationType.BLURAY, OfferPresentationType.DVD],
	[BuyboxOfferCategory.BUNDLE]: [OfferPresentationType.HD, OfferPresentationType.SD, OfferPresentationType._4K],
	[BuyboxOfferCategory.CINEMA]: [OfferPresentationType.HD],
};

const MONETIZATION_LABEL_TRANSLATABLES = {
	[OfferMonetizationType.FLATRATE]: 'WEBAPP_FLAT',
	[OfferMonetizationType.FLATRATE_AND_BUY]: 'WEBAPP_FLAT',
	[OfferMonetizationType.ADS]: 'WEBAPP_ADS',
	[OfferMonetizationType.FREE]: 'WEBAPP_FREE',
	[OfferMonetizationType.BUNDLE]: 'WEBAPP_FLAT',
};

const getBuyboxOffers = (
	offers: Offer[],
	buyboxCategories: BuyboxOfferCategory[],
	language: string, // used for price formatting
	country: string, // used for price formatting
	objectType: TitleObjectType,
	providersById: Record<string, Provider>,
	selectedPresentationType?: OfferPresentationType,
	domainSessionId?: string,
	promoted: BuyboxParam = BuyboxParam.Normal,
	allowedProviderIds: number[] = [] // filter by specific providers
): BuyboxOffers => {
	// FILTER and SORTING
	return buyboxCategories.reduce((root, buyboxCategory) => {
		root[buyboxCategory] =
			offers &&
			offers
				// filter monetization types
				.filter(offer => CATEGORY_MONETIZATIONS[buyboxCategory].includes(offer.monetization_type))
				// filter presentation types (for physical mainly)
				.filter(offer => {
					return (CATEGORY_PRESENTATION_TYPES[buyboxCategory] || []).includes(offer.presentation_type);
				})
				// filter for selectedPresentationType if they're not physical category
				.filter(offer => {
					// ignore selectedPresentationType for physical category
					if (![BuyboxOfferCategory.PHYSICAL].includes(buyboxCategory)) {
						if (<string>selectedPresentationType === OfferMonetizationType.FREE) {
							return [OfferMonetizationType.FREE, OfferMonetizationType.ADS].includes(
								offer.monetization_type
							);
						}

						// for non-4k flatrate offers, we want to show hd and sd anyway (because some can only be scraped in hd, but not sd and vice versa).
						// only when 4k presentation type is selected, it should filter specifically for it
						return (
							!selectedPresentationType ||
							offer.presentation_type === selectedPresentationType ||
							(offer.monetization_type === OfferMonetizationType.FLATRATE &&
								offer.presentation_type !== OfferPresentationType._4K &&
								selectedPresentationType !== OfferPresentationType._4K)
						);
					}
					return true;
				})
				.filter(offer => offer.provider_id in providersById)
				.filter(
					offer => allowedProviderIds.length === 0 || allowedProviderIds.indexOf(offer.provider_id) !== -1
				)
				// SORTING by lowest price and highest provider priority
				.sort((a, b) => {
					// prettier-ignore
					return [
                        {
                            // amount of seasons / episodes
                            score: 0 - ((a.element_count || 0) - (b.element_count || 0)),
                            weight: 10000,
                        },
                        {
                            // best price
                            score: ((a.type !== 'aggregated') ? (a.retail_price || 0) : 0) - ((b.type !== 'aggregated') ? (b.retail_price || 0) : 0),
                            weight: 100,
                        },
                        {
                            // selectedPresentationType
                            score: (a.presentation_type === selectedPresentationType ? 0 : 1) - (b.presentation_type === selectedPresentationType ? 0 : 1),
                            weight: 10,
                        },
                        {
                            // base presentation type
                            score: PRESENTATION_TYPE_PRIORITY.indexOf(a.presentation_type) - PRESENTATION_TYPE_PRIORITY.indexOf(b.presentation_type),
                            weight: 0.1,
                        },
                    ].reduce((root, item) => root + (item.score * item.weight), 0);
				})
				// don't allow duplicates (same provider_id + category) except for physical category
				.filter((offer, index, offers) => {
					return (
						[BuyboxOfferCategory.PHYSICAL].indexOf(buyboxCategory) !== -1 ||
						offers.findIndex(o => o.provider_id === offer.provider_id) === index
					);
				})
				// TRANSFORMING into BuyboxOffer format
				.map(offer => {
					const url = getUctLink(offer, providersById[offer.provider_id], country, promoted, domainSessionId);
					const item = {
						provider: providersById[offer.provider_id],
						offer, // full original offer
						price: getPriceForOffer(offer, `${language}-${country}`),
						translatable: getTranslatableForOffer(offer, objectType),
						presentationType: offer.presentation_type,
						url, // @TODO: Get UCT URL!
					};
					return item;
				});
		return root;
	}, {} as BuyboxOffers);
};

const getBuyboxCategoryByMonetization = (monetizationType: OfferMonetizationType): BuyboxOfferCategory | undefined => {
	return (Object.keys(CATEGORY_MONETIZATIONS) as (keyof typeof CATEGORY_MONETIZATIONS)[]).find(
		key => CATEGORY_MONETIZATIONS[key].indexOf(monetizationType) !== -1
	);
};

const hasStreamingOffers = (offers: Offer[]): boolean => {
	return offers.length > 0;
};

const trackClickOut = (
	offer: Offer,
	provider: Provider,
	objectType: TitleObjectType,
	objectId: number,
	seasonNumber?: number | null,
	episodeNumber?: number | null,
	offerLocation?: OfferLocation,
	additionalContexts?: SnowplowContext[],
	sportObjectType?: SportObjectType
) => {
	// A bit hacky but for clickouts, we do a double tracking.
	// We track in Snowplow (slow) and directly in the consumer api (real time).
	const contexts: SnowplowContext[] = [
		SnowplowClickoutContext.fromProviderOffer(offer, provider, sportObjectType),
		new SnowplowTitleContext(objectId, objectType, seasonNumber, episodeNumber),
		...(additionalContexts ? additionalContexts : []),
	];

	TrackingHelper.trackEvent(
		'clickout',
		{
			action: offer.monetization_type,
			label: provider.clearName,
			property: offer.presentation_type,
			value: offerLocation ?? OfferLocation.REGULAR_BUYBOX,
		},
		contexts
	);
};

export { getBuyboxCategoryByMonetization, getBuyboxOffers, hasStreamingOffers, trackClickOut };
